import { Component } from '@theme/component';
import { ThemeEvents, MediaStartedPlayingEvent, MediaStartedPlayedEvent, DialogOpenEvent, DialogCloseEvent } from '@theme/events';
import { observerElement } from '@theme/utilities';

/**
 * Applies the active/inactive playback policy to a single `<deferred-media>`.
 * Carousel, media-gallery and tabs each iterate their own container and decide
 * which item is active, then delegate the per-item decision here.
 *
 * @param {Element & { loadContent?: Function, playMedia?: Function, pauseMedia?: Function }} media
 * @param {Object} options
 * @param {boolean} options.isActive - Whether the media's slide/panel is active.
 * @param {boolean} options.shouldAutoplay - Whether this media is configured to autoplay.
 * @param {boolean} [options.canLoad=true] - Lazy-load the deferred content when active + autoplay.
 * @param {boolean} [options.resumeNonAutoplay=true] - Play active media even when it isn't an autoplay item.
 */
export function syncDeferredMedia(media, { isActive, shouldAutoplay, canLoad = true, resumeNonAutoplay = true }) {
  const isLoaded = media.hasAttribute('data-media-loaded');

  if (isActive) {
    if (canLoad && shouldAutoplay && !isLoaded) {
      media.loadContent?.(false, true);
    } else if (shouldAutoplay || resumeNonAutoplay) {
      media.playMedia?.();
    }
  } else if (isLoaded || !canLoad) {
    media.pauseMedia?.();
  }
}

/**
 * A deferred media element that extends Component for better DOM management
 * @typedef {Object} DeferredMediaRefs
 * @property {HTMLElement} toggleMediaButton - The button to toggle the media
 */
class DeferredMedia extends Component {
  /** @type {boolean} */
  isPlaying = false;

  /** @type {boolean} */
  #isLazyLoad = false;

  /** @type {boolean} */
  #isAutoplay = false;

  /** @type {boolean} */
  #multipleVideosPlay = false;

  /** @type {IntersectionObserver|null} */
  #scrollObserver = null;

  /** @type {boolean} - Set when an overlay silenced this media, so only it gets restored. */
  #pausedByDialog = false;

  connectedCallback() {
    super.connectedCallback();
    
    // Initialize lazy load and autoplay settings
    this.#isLazyLoad = this.hasAttribute('data-lazy-load');
    this.#isAutoplay = this.hasAttribute('data-autoplay');
    this.#multipleVideosPlay = this.hasAttribute('data-multiple-videos-play');
    
    // Listen for media started playing events to pause other media (never pause self)
    if (! this.#isAutoplay && ! this.#multipleVideosPlay) {
      this.boundPauseMedia = (event) => {
        if (event.detail?.resource === this) return;
        this.pauseMedia();
      };
      document.addEventListener(ThemeEvents.mediaStartedPlaying, this.boundPauseMedia);
    }

    const { signal } = this.abortController;
    document.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal });
    document.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

    this.#initialize();
  }

  // Reads the element itself, since native controls play and pause without going through playMedia/pauseMedia. An embed exposes no state, so its flag is all we have.
  get #isActuallyPlaying() {
    const video = this.querySelector('video');
    if (video) return !video.paused && !video.ended;
    return this.isPlaying;
  }

  // Stops playback the moment an overlay covers the page; media inside an open overlay is left alone.
  #handleDialogOpen = () => {
    if (!this.#isActuallyPlaying || this.closest('.dialog[open]')) return;
    this.#pausedByDialog = true;
    this.pauseMedia();
  };

  // Hands back exactly what an overlay silenced, once every overlay is gone. Dismissing an overlay is a deliberate return to the page, unlike scrolling past it, so the video the shopper was watching picks up again.
  #handleDialogClose = () => {
    if (!this.#pausedByDialog || document.querySelector('.dialog[open]')) return;
    this.#pausedByDialog = false;
    this.playMedia();
  };

  /**
   * Lazy load (IntersectionObserver) or load immediately when autoplay.
   */
  #initialize() {
    this.#scrollObserver?.disconnect();
    this.#scrollObserver = null;

    this.#scrollObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (this.#isAutoplay) {
            if (!this.hasAttribute('data-media-loaded')) {
              this.loadContent(false);
            } else {
              this.playMedia();
            }
          }
          // Non-autoplay: do not auto-play on scroll-in
        } else {
          this.pauseMedia();
        }
      });
    }, { threshold: 0 });
    this.#scrollObserver.observe(this);

    if (!this.#isAutoplay && this.#isLazyLoad) {
      observerElement({
        element: this,
        callback: () => this.loadContent(false),
        threshold: '100',
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.boundPauseMedia) {
      document.removeEventListener(ThemeEvents.mediaStartedPlaying, this.boundPauseMedia);
    }
    this.#scrollObserver?.disconnect();
    this.#scrollObserver = null;
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initialize();
  }

  /**
   * Updates the visual hint for play/pause state
   * @param {boolean} isPlaying - Whether the video is currently playing
   */
  updatePlayPauseHint(isPlaying) {
    const toggleMediaButton = this.refs.toggleMediaButton;
    if (toggleMediaButton instanceof HTMLElement) {
      toggleMediaButton.classList.remove('hidden');
      const playIcon = toggleMediaButton.querySelector('.icon-play');
      if (playIcon) playIcon.classList.toggle('hidden', isPlaying);
      const pauseIcon = toggleMediaButton.querySelector('.icon-pause');
      if (pauseIcon) pauseIcon.classList.toggle('hidden', !isPlaying);
    }
  }

  /**
   * Shows the deferred media content
   */
  showDeferredMedia = () => {
    this.loadContent(true);
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying, true);
  };

  /**
   * Loads the content
   * @param {boolean} [focus] - Whether to focus the content
   * @param {boolean} [muted] - Whether to mute the content
   */
  loadContent(focus = true, muted = false) {
    if (this.getAttribute('data-media-loaded')){
      return;
    }

    this.dispatchEvent(new MediaStartedPlayingEvent(this));

    const content = this.querySelector('template')?.content.firstElementChild?.cloneNode(true);
    if (!content) return;

    this.setAttribute('data-media-loaded', 'true');
    this.appendChild(content);

    if (focus && content instanceof HTMLElement) {
      content.focus();
    }

    if (content instanceof HTMLVideoElement) {
      const videoAlt = content.getAttribute('aria_label');
      const videoFallbackImg = content.querySelector('img');
      if (videoAlt && videoFallbackImg) {
        videoFallbackImg.setAttribute('alt', videoAlt);
      }

      if (this.#isAutoplay) {
        // Use 'playing' so the first frame is rendered before the crossfade starts
        content.addEventListener('playing', () => {
          this.classList.add('deferred-media--loaded', 'deferred-media--playing');
        }, { once: true });
        content.muted = true;
        content.play().catch(() => {});
      } else {
        content.addEventListener('loadeddata', () => {
          this.classList.add('deferred-media--loaded');
        }, { once: true });
        content.addEventListener('playing', () => {
          this.classList.add('deferred-media--playing');
        }, { once: true });
        content.muted = muted;
        content.play().catch(() => {});
      }

      content.addEventListener('play', () => {
        this.dispatchEvent(new MediaStartedPlayingEvent(this));
      });

      this.isPlaying = true;
    } else {
      this.classList.add('deferred-media--loaded', 'deferred-media--playing');
    }

    this.dispatchEvent(new MediaStartedPlayedEvent(this));
  }

  /**
   * Releases a loaded video so the platform reclaims its decoder. loadContent clones from the
   * template rather than consuming it, so clearing the loaded flag lets it rebuild on demand.
   */
  unloadContent() {
    if (!this.hasAttribute('data-media-loaded')) return;

    const video = this.querySelector('video');
    if (video) {
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.removeAttribute('src'));
      video.load();
      video.remove();
    }

    this.removeAttribute('data-media-loaded');
    this.classList.remove('deferred-media--loaded', 'deferred-media--playing');
    this.isPlaying = false;
  }

  /**
   * Toggle play/pause state of the media
   */
  toggleMedia() {
    this.isPlaying ? this.pauseMedia() : this.playMedia();
  }

  /**
   * Plays the media
   */
  playMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');
    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"playVideo","args":""}'
          : '{"method":"play"}',
        '*'
      );
    } else {
      const video = this.querySelector('video');
      if (video) {
        video.play().catch(() => {
          video.addEventListener('canplay', () => video.play().catch(() => {}), { once: true });
        });
      }
    }
    this.isPlaying = true;
    this.updatePlayPauseHint(this.isPlaying);
    this.dispatchEvent(new MediaStartedPlayingEvent(this));
  }

  /**
   * Pauses the media
   */
  pauseMedia() {
    /** @type {HTMLIFrameElement | null} */
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      iframe.contentWindow?.postMessage(
        iframe.dataset.videoType === 'youtube'
          ? '{"event":"command","func":"' + 'pauseVideo' + '","args":""}'
          : '{"method":"pause"}',
        '*'
      );
    } else {
      this.querySelector('video')?.pause();
    }
    this.isPlaying = false;

    // If we've already revealed the deferred media, we should toggle the play/pause hint
    if (this.getAttribute('data-media-loaded')) {
      this.updatePlayPauseHint(this.isPlaying);
    }
  }

  /**
   * Mutes or unmutes the media
   * Supports both iframe videos (YouTube/Vimeo) and HTML5 video elements
   * @param {boolean} [mute=true] - Whether to mute (true) or unmute (false)
   */
  muteMedia(mute = true) {
    const iframe = this.querySelector('iframe[data-video-type]');

    if (iframe) {
      const command =
        iframe.dataset.videoType === 'youtube'
          ? JSON.stringify({ event: 'command', func: mute ? 'muteVideo' : 'unMuteVideo', args: [] })
          : JSON.stringify({ method: mute ? 'mute' : 'unMute' });

      iframe.contentWindow?.postMessage(command, '*');
      setTimeout(() => iframe.contentWindow?.postMessage(command, '*'), 1000);
    } else {
      const video = this.querySelector('video');
      if (video && video instanceof HTMLVideoElement) {
        video.muted = mute;
      }
    }
  }
}

if (!customElements.get('deferred-media')) {
  customElements.define('deferred-media', DeferredMedia);
}

/**
 * A product model
 */
class ProductModel extends DeferredMedia {

  loadContent() {
    super.loadContent();

    Shopify.loadFeatures([
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: this.setupModelViewerUI.bind(this),
      },
    ]);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const element = this.querySelector('model-viewer');
    if (element && this.handlePointerDown) {
      element.removeEventListener('pointerdown', this.handlePointerDown);
    }
  }

  pauseMedia() {
    super.pauseMedia();
    this.modelViewerUI?.pause();
  }

  playMedia() {
    super.playMedia();
    this.modelViewerUI?.play();
  }

  /**
   * @param {Error[]} errors
   */
  async setupModelViewerUI(errors) {
    if (errors) return;

    if (!Shopify.ModelViewerUI) {
      await this.#waitForModelViewerUI();
    }

    if (!Shopify.ModelViewerUI) return;

    const element = this.querySelector('model-viewer');
    if (!element) return;

    this.modelViewerUI = new Shopify.ModelViewerUI(element);
    if (!this.modelViewerUI) return;

    this.playMedia();

    // Track pointer events to detect taps
    let pointerStartX = 0;
    let pointerStartY = 0;

    this.handlePointerDown = (/** @type {PointerEvent} */ event) => {
      pointerStartX = event.clientX;
      pointerStartY = event.clientY;
      // Interacting with the model claims active playback so other media pause.
      this.dispatchEvent(new MediaStartedPlayingEvent(this));
    };
    element.addEventListener('pointerdown', this.handlePointerDown);
    
  }

  /**
   * Waits for Shopify.ModelViewerUI to be defined.
   * This seems to be necessary for Safari since Shopify.ModelViewerUI is always undefined on the first try.
   * @returns {Promise<void>}
   */
  async #waitForModelViewerUI() {
    const maxAttempts = 10;
    const interval = 50;

    for (let i = 0; i < maxAttempts; i++) {
      if (Shopify.ModelViewerUI) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, interval));
    }
  }
}

if (!customElements.get('product-model')) {
  customElements.define('product-model', ProductModel);
}
